
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { KeyRequestsTable } from "./KeyRequestsTable";
import { KeyRequestsDialog } from "./KeyRequestsDialog";
import { useKeyAsks } from "../hooks/useKeyAsks";
import { Account, KeyAsk } from "../types";
import { toast } from "sonner";

interface KeyRequestsSectionProps {
  selectedAccount: Account | null;
  canEdit: boolean;
}

export const KeyRequestsSection = ({ selectedAccount, canEdit }: KeyRequestsSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAsk, setEditingAsk] = useState<KeyAsk | null>(null);
  
  const { keyAsks, isLoading, addKeyAsk, updateKeyAsk, deleteKeyAsk } = useKeyAsks(selectedAccount);

  const handleAddNew = () => {
    if (!canEdit) {
      toast.error("Only account owners can add key requests");
      return;
    }
    setEditingAsk(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (ask: KeyAsk) => {
    if (!canEdit) {
      toast.error("Only account owners can edit key requests");
      return;
    }
    setEditingAsk(ask);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!canEdit) {
      toast.error("Only account owners can delete key requests");
      return;
    }
    await deleteKeyAsk(id);
  };

  const handleSave = async (askData: Omit<KeyAsk, 'id'>) => {
    if (!canEdit) {
      toast.error("Only account owners can save key requests");
      return;
    }

    let success = false;
    if (editingAsk) {
      success = await updateKeyAsk(editingAsk.id, askData);
    } else {
      success = await addKeyAsk(askData);
    }

    if (success) {
      setIsDialogOpen(false);
      setEditingAsk(null);
    }
  };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">Key Requests</CardTitle>
          <Button
            variant="outline"
            className="border-primary/20 bg-primary hover:bg-primary/90 text-black"
            onClick={handleAddNew}
            disabled={!canEdit}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Request
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <KeyRequestsTable
          keyAsks={keyAsks}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canEdit}
        />
        
        <KeyRequestsDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSave}
          editingAsk={editingAsk}
        />
      </CardContent>
    </Card>
  );
};
